// frontend/src/components/PwaStartupGate.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Created:      2026-10-19
// Updated:      2026-10-19 15:10 BST
//
// Description:
//   Startup-only branded screen for initial authentication/bootstrap.
//   Uses public hostname-resolved PWA artwork; never trusts browser tenant IDs.
//   Does not run on intra-app navigation or artificially prolong startup.
//   Native Android/iOS splash screens remain platform-controlled (Stage 6).

import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

const FALLBACK_COLOR = "#172d41";
const BRANDING_ENDPOINT = "/api/pwa/startup-branding";
const REQUEST_DEADLINE_MS = 2000; // Failure bound, not a minimum display duration.

function safeBackground(value) {
  return /^#[0-9a-fA-F]{6}$/.test(value || "") ? value : FALLBACK_COLOR;
}

function safeAsset(value) {
  if (typeof value !== "string" || !value.startsWith("/api/pwa/splash/")) return null;
  return value;
}

export default function PwaStartupGate({ children }) {
  const { loading } = useAuth();
  const [brand, setBrand] = useState(null);
  const [brandingPending, setBrandingPending] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    const deadline = window.setTimeout(() => controller.abort(), REQUEST_DEADLINE_MS);

    fetch(BRANDING_ENDPOINT, {
      credentials: "same-origin",
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Startup branding HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (live) setBrand(payload);
      })
      .catch(() => {
        // Unmapped/disabled PWA or offline: continue with fallback, not an error page.
      })
      .finally(() => {
        if (live) setBrandingPending(false);
      });

    return () => {
      live = false;
      window.clearTimeout(deadline);
      controller.abort();
    };
  }, []);

  if (!loading && !brandingPending) return children;

  const backgroundColor = safeBackground(brand?.background_color);
  const backgroundImage = safeAsset(brand?.splash_background_url);
  const logo = safeAsset(brand?.splash_logo_url);
  const name = typeof brand?.app_name === "string" && brand.app_name.trim()
    ? brand.app_name.trim()
    : "Vision Systems";

  return (
    <main
      role="status"
      aria-live="polite"
      aria-label="Vision Systems starting"
      style={{
        position: "fixed", inset: 0, zIndex: 2147483647,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", backgroundColor,
        backgroundImage: backgroundImage ? `url("${backgroundImage}")` : "none",
        backgroundPosition: "center", backgroundSize: "cover", backgroundRepeat: "no-repeat",
        padding: "max(24px, env(safe-area-inset-top)) max(24px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left))",
        boxSizing: "border-box", fontFamily: "Arial, sans-serif",
      }}
    >
      {logo ? (
        <img src={logo} alt={name} style={{ width: "min(72vw, 340px)", maxHeight: "42vh", objectFit: "contain" }} />
      ) : (
        <div style={{ color: "#fff", fontSize: "clamp(22px, 6vw, 36px)", fontWeight: 600, textAlign: "center", letterSpacing: "0.08em" }}>
          {name}
        </div>
      )}
    </main>
  );
}
